import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Spin, Result, Button, Card, Typography, Space, message } from 'antd';
import { connect } from 'react-redux';
import { requestServerPost, loadUserData, selectPage } from '../../actions';
import StringHelper from '../../helpers/StringHelper';
import ChangePasswordModal from '../../components/popups/ChangePasswordModal';
import Constants from '../../data/constants';

const { Title, Text, Paragraph } = Typography;

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const VerifyLandingPage = ({ requestServerPost, loadUserData, selectPage }) => {
    const query = useQuery();
    const history = useHistory();

    // URL Parameters
    const dataParam = query.get('data');
    const timestampParam = query.get('timestamp');

    // State
    const [status, setStatus] = useState('LANDED'); // LANDED, VERIFYING, SUCCESS_OPTIONS, ERROR
    const [decryptedInfo, setDecryptedInfo] = useState(null);
    const [apiResponse, setApiResponse] = useState(null); // Stores token and user info from confirm_email
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!dataParam || !timestampParam) {
            setStatus('ERROR');
            setErrorMsg('Invalid verification link. Missing parameters.');
            return;
        }

        try {
            // Fix URL decoding issue where '+' is replaced by ' '
            const fixedDataParam = dataParam.replace(/ /g, '+');

            // Decrypt data just to show info or validate (optional but requested)
            // Note: The actual API call sends the encrypted data/timestamp as is.
            console.log("Data Param:", fixedDataParam);
            console.log("Timestamp Param:", timestampParam);
            const decrypted = StringHelper.decryptWithTimeStamp(fixedDataParam, timestampParam);
            // console.log("Decrypted Data:", decrypted);

            // Try to parse if it's JSON, otherwise keep as string
            try {
                const parsed = JSON.parse(decrypted);
                setDecryptedInfo(parsed);
            } catch (e) {
                setDecryptedInfo(decrypted);
            }

        } catch (err) {
            console.error("Decryption failed", err);
            // Don't block the UI strictly on decryption fail if we want to let the server decide verify status, 
            // but usually if client decryption fails, something is wrong. But we can still allow the user to click verify.
        }
    }, [dataParam, timestampParam]);

    const handleVerifyClick = () => {
        setLoading(true);
        setStatus('VERIFYING');

        // Fix URL decoding issue where '+' is replaced by ' '
        const fixedDataParam = dataParam ? dataParam.replace(/ /g, '+') : "";

        // Prepare params as received
        const params = {
            data: fixedDataParam,
            timestamp: timestampParam
        };

        requestServerPost(
            'confirm_email',
            params,
            {},
            (response) => {
                setLoading(false);
                if (response.data && response.data.status === 'success') {
                    // Success! Store response data (token, user)
                    console.log("Response Data:", response.data);

                    let finalResponseData = response.data;
                    try {
                        // The response data is encrypted just like the URL params
                        if (response.data.data && response.data.timestamp) {
                            const decryptedString = StringHelper.decryptWithTimeStamp(
                                response.data.data,
                                response.data.timestamp
                            );
                            const decryptedJson = JSON.parse(decryptedString);
                            // console.log("Decrypted Response:", decryptedJson);
                            finalResponseData = decryptedJson;
                        }
                    } catch (e) {
                        console.error("Failed to decrypt response", e);
                        message.error("Failed to process server response.");
                        return; // Stop if decryption fails? Or proceed with raw data? Safest to stop or show error.
                    }

                    setApiResponse(finalResponseData);
                    setStatus('SUCCESS_OPTIONS');
                    message.success("Email verified successfully!");
                } else {
                    console.log("Response Data:", response.data);
                    setStatus('ERROR');
                    setErrorMsg(response.data?.message || "Verification failed. Please try again.");
                }
            },
            "", // No user token needed for public verification endpoint usually? Or maybe one is extracted?
            // The prompt says "use the token that is sent in the response of the api" for later steps.
            // For this step, we likely don't have a token yet unless confirm_email is public.
            // Assuming public or handled by backend via the query params.
            {},
            (err) => {
                setLoading(false);
                setStatus('ERROR');
                setErrorMsg("Unable to verify. Please try again.");
            }
        );
    };

    const handleLoginDirectly = () => {
        if (apiResponse && apiResponse.token) {
            // Load user data into Redux/App state
            // apiResponse is the full object { user: {...}, token: "..." }
            loadUserData(apiResponse);

            // Persist to localStorage to remain logged in on refresh
            // Replicating the encoding logic from reducers/index.js
            try {
                // Assuming Buffer is available globally as in reducers, otherwise fallback
                const encodedString = new Buffer(JSON.stringify(apiResponse)).toString('base64');
                localStorage.setItem("userdata", encodedString);
            } catch (e) {
                console.warn("Buffer not available, falling back to btoa");
                localStorage.setItem("userdata", btoa(JSON.stringify(apiResponse)));
            }

            // Navigate to Brand Page
            selectPage(Constants.BRAND_PAGE);
            history.push('/brand');
        } else {
            message.error("Invalid session data. Please login manually.");
            history.push('/login');
        }
    };

    const handleChangePasswordComplete = () => {
        setShowChangePassword(false);
        // After password change, redirect to login page
        history.push('/login');
    };

    const renderContent = () => {
        switch (status) {
            case 'LANDED':
                return (
                    <Card style={{ width: 400, textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <Title level={3}>Verify Your Email</Title>
                        <Paragraph>
                            Please click the button below to verify your email address and activate your account.
                        </Paragraph>
                        {/* {decryptedInfo && (
                            <div style={{ marginBottom: 20, textAlign: 'left', background: '#f5f5f5', padding: 10, borderRadius: 4 }}>
                                <Text strong>Verifying for:</Text><br/>
                                <Text type="secondary">{typeof decryptedInfo === 'object' ? decryptedInfo.email || JSON.stringify(decryptedInfo) : decryptedInfo}</Text>
                            </div>
                        )} */}
                        <Button type="primary" size="large" onClick={handleVerifyClick} loading={loading} block>
                            Verify Email
                        </Button>
                    </Card>
                );
            case 'VERIFYING':
                return (
                    <Spin size="large" tip="Verifying your email..." />
                );
            case 'SUCCESS_OPTIONS':
                return (
                    <Card style={{ width: 500, textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <Result
                            status="success"
                            title="Email Verified Successfully!"
                            subTitle="Your account is now active. You can proceed to the dashboard or set a new password."
                        />
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Button type="primary" size="large" onClick={handleLoginDirectly} block>
                                Login & Continue
                            </Button>
                            <Button type="default" size="large" onClick={() => setShowChangePassword(true)} block>
                                Change Password
                            </Button>
                        </Space>
                    </Card>
                );
            case 'ERROR':
                return (
                    <Card style={{ width: 400, textAlign: 'center' }}>
                        <Result
                            status="error"
                            title="Verification Failed"
                            subTitle={errorMsg}
                            extra={[
                                <Button type="primary" key="retry" onClick={handleVerifyClick} style={{ marginRight: 10 }}>
                                    Retry
                                </Button>,
                                <Button key="login" onClick={() => history.push('/login')}>
                                    Go to Login
                                </Button>
                            ]}
                        />
                    </Card>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            background: '#f0f2f5',
            flexDirection: 'column'
        }}>
            {renderContent()}

            {/* Change Password Modal */}
            {apiResponse && (
                <ChangePasswordModal
                    visible={showChangePassword}
                    onCancel={() => setShowChangePassword(false)}
                    onComplete={handleChangePasswordComplete}
                    requestServerPost={requestServerPost} // Pass the action creator
                    usertoken={apiResponse.token} // Use token from response
                    user={apiResponse.user}      // Use user object from response
                    successTitle="Password Changed"
                    successContent="Your password has been changed successfully. Please login with your new password."
                    successOkText="Go to Login"
                />
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    // Map any needed state (api response often handled via callbacks, so maybe none needed here)
});

export default connect(mapStateToProps, { requestServerPost, loadUserData, selectPage })(VerifyLandingPage);
